import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/api";
import { useI18n } from "@/i18n";

const LANG_LABEL = { fr: "FR", en: "EN", tr: "TR" };

/**
 * Display user-generated text with a one-click translation button.
 *
 * Props:
 *   text:       The original string to display.
 *   className:  Optional className on the outer text block.
 *   style:      Optional inline styles on the outer text block.
 *   minLength:  Don't show the Translate link below this length (default 8).
 *
 * Behaviour:
 *   - Renders the raw text by default.
 *   - Below the text, shows a discreet "🌐 Translate to {lang}" button.
 *   - When clicked, calls /api/translate and renders the translation
 *     in a subtle inset box, plus a "Hide translation" toggle.
 *   - If Claude reports the text is already in the target language,
 *     shows a discreet "Already in {lang}" hint.
 */
export default function TranslatableText({ text, className = "", style, minLength = 8 }) {
  const { lang } = useI18n();
  const [translated, setTranslated] = useState(null);
  const [busy, setBusy] = useState(false);

  const L = (fr, en, tr) => (lang === "fr" ? fr : lang === "tr" ? tr : en);

  const translate = async () => {
    if (!text) return;
    setBusy(true);
    try {
      const r = await api.post("/translate", { texts: [text], target_lang: lang });
      const t0 = r.data.translations?.[0];
      setTranslated({
        text: t0 || text,
        sameAsOriginal: !t0 || t0.trim() === text.trim(),
      });
    } catch (e) {
      toast.error(L("Échec de la traduction", "Translation failed", "Çeviri başarısız"));
    } finally {
      setBusy(false);
    }
  };

  if (!text) {
    return <span className={className} style={style}>—</span>;
  }

  const showButton = text.trim().length >= minLength;

  return (
    <div>
      <div className={className} style={{ whiteSpace: "pre-wrap", ...style }} data-testid="translatable-text">
        {text}
      </div>
      {translated && !translated.sameAsOriginal && (
        <div
          className="mt-2 p-2 rounded-[8px] text-[13px]"
          style={{ background: "var(--bg-subtle)", border: "1px dashed var(--border)", whiteSpace: "pre-wrap" }}
          data-testid="translatable-translated"
        >
          <div className="text-[10px] font-mono uppercase tracking-wide mb-1" style={{ color: "var(--text-tertiary)" }}>
            {L("Traduction", "Translation", "Çeviri")} · {LANG_LABEL[lang]}
          </div>
          {translated.text}
        </div>
      )}
      {showButton && (
        <div className="mt-1">
          {translated ? (
            <button
              onClick={() => setTranslated(null)}
              className="text-[10.5px] hover:underline"
              style={{ color: "var(--text-tertiary)" }}
              data-testid="translatable-hide"
            >
              {translated.sameAsOriginal
                ? L("✓ Déjà dans la bonne langue", "✓ Already in the target language", "✓ Zaten doğru dilde")
                : L("Masquer la traduction", "Hide translation", "Çeviriyi gizle")}
            </button>
          ) : (
            <button
              onClick={translate}
              disabled={busy}
              className="text-[10.5px] hover:underline inline-flex items-center gap-1"
              style={{ color: "var(--text-tertiary)" }}
              data-testid="translatable-trigger"
            >
              {busy
                ? L("Traduction…", "Translating…", "Çeviriliyor…")
                : L(`🌐 Traduire en ${LANG_LABEL[lang]}`, `🌐 Translate to ${LANG_LABEL[lang]}`, `🌐 ${LANG_LABEL[lang]}'ye çevir`)}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
